import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Box, useMediaQuery, useTheme, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as ManualBear } from '../../assets/images/manual_bear.svg';
import { useSelector } from 'react-redux';
import { getSiteManualUrl } from '../../helpers/siteManual';

const HEADER_HEIGHT = 64;
const DESKTOP_BEAR_SIZE = 140;
const MOBILE_BEAR_SIZE = 96;

const FloatingBear = ({ isLoginPage }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const bearSize = isMobile ? MOBILE_BEAR_SIZE : DESKTOP_BEAR_SIZE;
    const profile = useSelector((state) => state.user.profile);

    const [isVisible, setIsVisible] = useState(true);
    const [isPinned, setIsPinned] = useState(false);
    const [bear, setBear] = useState({
        x: Math.random() * (window.innerWidth - 80),
        y: HEADER_HEIGHT + Math.random() * (window.innerHeight - HEADER_HEIGHT - 100),
        vx: (Math.random() - 0.5) * 4 || 2,
        vy: (Math.random() - 0.5) * 4 || 2
    });

    const animationRef = useRef(null);
    const bearRef = useRef(bear);

    const handleOpenManual = (e) => {
        e.stopPropagation();

        window.open(getSiteManualUrl(profile), '_blank', 'noopener,noreferrer');
    };

    useEffect(() => {
        if (!isLoginPage || isPinned) return;

        const handleGlobalClick = (e) => {
            if (e.target.closest('.close-bear-btn') || e.target.closest('.bear-content')) return;
            setIsPinned(true);
        };

        window.addEventListener('click', handleGlobalClick);
        return () => window.removeEventListener('click', handleGlobalClick);
    }, [isLoginPage, isPinned]);

    useEffect(() => {
        if (!isLoginPage || isPinned || !isVisible) {
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
            return;
        }

        const animate = () => {
            const current = bearRef.current;
            let newX = current.x + current.vx;
            let newY = current.y + current.vy;
            let newVx = current.vx;
            let newVy = current.vy;

            if (newX <= 0 || newX >= window.innerWidth - bearSize) {
                newVx = -newVx;
                newX = Math.max(0, Math.min(newX, window.innerWidth - bearSize));
            }
            if (newY <= HEADER_HEIGHT || newY >= window.innerHeight - bearSize) {
                newVy = -newVy;
                newY = Math.max(HEADER_HEIGHT, Math.min(newY, window.innerHeight - bearSize));
            }

            const newBear = { x: newX, y: newY, vx: newVx, vy: newVy };
            bearRef.current = newBear;
            setBear(newBear);
            animationRef.current = requestAnimationFrame(animate);
        };

        animationRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animationRef.current);
    }, [isLoginPage, isPinned, isVisible, bearSize]);

    if (!isVisible) return null;

    const shouldBeAtCorner = !isLoginPage || isMobile || isPinned;

    const bearStyle = !shouldBeAtCorner
        ? {
              left: bear.x,
              top: bear.y,
              right: 'auto',
              bottom: 'auto',
              transition: 'none'
          }
        : {
              right: 10,
              bottom: 10,
              left: 'auto',
              top: 'auto',
              transition: 'all 0.8s cubic-bezier(0.25, 0.1, 0.25, 1)'
          };

    const bearElement = (
        <Box
            sx={{
                position: 'fixed',
                zIndex: 1100,
                pointerEvents: 'none',
                willChange: !shouldBeAtCorner ? 'left, top' : 'auto',
                ...bearStyle,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end'
            }}
        >
            <IconButton
                className="close-bear-btn"
                onClick={(e) => {
                    e.stopPropagation();
                    setIsVisible(false);
                }}
                sx={{
                    pointerEvents: 'auto',
                    backgroundColor: 'rgba(0,0,0,0.4)',
                    color: 'white',
                    padding: '2px',
                    mb: '-10px',
                    mr: '10px',
                    zIndex: 1101,
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' }
                }}
            >
                <CloseIcon sx={{ fontSize: isMobile ? 16 : 20 }} />
            </IconButton>

            <Box
                className="bear-content"
                onClick={handleOpenManual}
                sx={{
                    pointerEvents: 'auto',
                    cursor: 'pointer',
                    '&:hover': {
                        transform: 'scale(1.05)',
                        filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.2))'
                    },
                    transition: 'transform 0.2s'
                }}
            >
                <ManualBear width={bearSize} height={bearSize} />
            </Box>
        </Box>
    );

    return createPortal(bearElement, document.body);
};

export default FloatingBear;
